// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { FeedbackLogs, getLogs } from '../utils/logger';
import { v1 as createGUID } from 'uuid';
import { BrbClient, BearerBrbCredential } from '@ic3/brb';

declare const CALLING_SDK_VERSION: string;
declare const BRB_BASE_URL: string;

export type Feedback = {
  callId: string;
  reporter: string;
  title: string;
  type: string;
  logs: FeedbackLogs;
  comments: string;
  feedbackId: string;
};

export const createFeedback = (
  callId: string,
  reporter: string,
  feedbackTitle: string,
  feedbackType: string,
  comment: string
): Feedback => {
  return {
    logs: getLogs(),
    callId: callId,
    reporter: reporter,
    title: feedbackTitle,
    type: feedbackType,
    comments: formatCommentForADO(comment),
    feedbackId: createGUID()
  };
};

/**
 * ADO and BRB both ignore new lines. This function replaces new lines with a <br /> tag.
 * This was the recommendation by the BRB team.
 */
const formatCommentForADO = (comment: string): string => comment.replace(/(\r\n|\n|\r)/gm, '<br />');

export const uploadFeedback = async (
  feedback: Feedback,
  optionalScreenshot: Blob | null,
  aadToken: string
): Promise<void> => {
  const client = new BrbClient(new BearerBrbCredential(aadToken), (opts) => {
    opts.baseUrl = BRB_BASE_URL;
  });
  const session = await client.openSession(
    {
      msAlias: feedback.reporter,
      title: feedback.title,
      description: feedback.comments,
      coreLibVersion: CALLING_SDK_VERSION,
      clientId: 'acs_web',
      clientVersion: '1.0.1',
      categoryName: feedback.type,
      skipTicketCreation: false,
      skipEmailNotification: false,
      adoTags: 'ACS_Selfhost_BRB',
      adoInstance: 'skype',
      adoProject: 'SPOOL',
      adoArea: 'SPOOL\\Shared\\Selfhost'
    },
    'acs'
  );

  try {
    const serializedFeedback = JSON.stringify(feedback, null, 4);
    await session.uploadData(
      `ACS-Calling-log-${session.containerId}.json`,
      new TextEncoder().encode(serializedFeedback)
    );
    if (optionalScreenshot) {
      const screenshotData = await optionalScreenshot.arrayBuffer();
      await session.uploadData(`ACS-Selfhost-BRB-screenshot-${session.containerId}.png`, screenshotData);
    }
  } finally {
    await session.close();
  }
};
